import {
  computed,
  effect,
  inject,
  Injectable,
  signal,
  untracked,
} from '@angular/core';
import { Observable, of, Subscription, tap } from 'rxjs';
import { NotificationService } from '../../../core/common/services/notification/notification.service';
import {
  Notification,
  NotificationResponse,
} from '../notification/notifications-type.enum';

/**
 * Notification list + websocket subscription shared by every bell (navbar and side menu).
 * Reference-counted so swapping one bell for the other never drops the subscription.
 */
@Injectable({ providedIn: 'root' })
export class NotificationFeedService {
  private readonly notificationService = inject(NotificationService);

  private readonly items = signal<Notification[]>([]);
  private readonly connections = signal(0);
  private loadSubscription?: Subscription;
  private lastMarkAsReadTrigger = 0;
  private lastNewNotificationTrigger = 0;

  readonly notifications = this.items.asReadonly();
  readonly hasUnread = computed(() => this.items().some(n => !n.read_at));
  readonly panelHeight = computed(() =>
    this.items().length > 0 ? '38.125rem' : '21.25rem'
  );

  constructor() {
    effect(() => {
      if (this.connections() === 0) {
        return;
      }
      const markAsRead = this.notificationService.markAsReadTrigger();
      const incoming = this.notificationService.newNotificationTrigger();
      untracked(() => {
        if (
          markAsRead !== this.lastMarkAsReadTrigger ||
          incoming !== this.lastNewNotificationTrigger
        ) {
          this.lastMarkAsReadTrigger = markAsRead;
          this.lastNewNotificationTrigger = incoming;
          this.load();
        }
      });
    });
  }

  connect(): void {
    this.connections.update(count => count + 1);
    if (this.connections() === 1) {
      this.load();
      this.notificationService.subscribeToNotifications();
    }
  }

  disconnect(): void {
    if (this.connections() === 0) {
      return;
    }
    this.connections.update(count => count - 1);
    if (this.connections() === 0) {
      this.loadSubscription?.unsubscribe();
      this.items.set([]);
      this.notificationService.unsubscribeFromNotifications();
    }
  }

  load(): void {
    // Cancel the previous request so a stale response can't overwrite a newer one
    // (or repopulate the feed after the last bell disconnected).
    this.loadSubscription?.unsubscribe();
    this.loadSubscription = this.notificationService
      .getNotifications(undefined, null, 10)
      .subscribe((response: NotificationResponse) =>
        this.items.set(response.data)
      );
  }

  /** Marks one notification read (no request when it already is) and updates the list. */
  markRead(notification: Notification): Observable<unknown> {
    if (notification.read_at) {
      return of(undefined);
    }
    return this.notificationService
      .markAsRead(notification.id)
      .pipe(tap(() => this.setRead(n => n.id === notification.id)));
  }

  markAllRead(): void {
    this.notificationService
      .markAllAsRead()
      .subscribe(() => this.setRead(() => true));
  }

  private setRead(match: (n: Notification) => boolean): void {
    const now = new Date().toISOString();
    this.items.update(list =>
      list.map(n => (match(n) ? { ...n, read_at: now } : n))
    );
  }
}
